import type { Value } from 'platejs';
import type { MentionUser } from './mention';

export type ToolbarVariant = 'minimal' | 'slim' | 'full';

export interface PlateEditorProps {
    className?: string;
    placeholder?: string;
    initialValue?: Value;
    readOnly?: boolean;
    hideToolbar?: boolean;
    fullHeight?: boolean;
    onChange?: (value: Value) => void;
    toolbarVariant?: ToolbarVariant;
    toolbarHostId?: string;
    onEditorMount?: (editor: any) => void;
    /**
     * Backend tag used when uploading pasted / dropped images. Threaded to
     * `uploadSingleImage(dataUrl, uploadSource)`. Default 'one-pager' for
     * back-compat with the original consumer; set per-consumer when reusing
     * the editor elsewhere (e.g. 'engagement-letter', 'application-form').
     */
    uploadSource?: string;
    /** Enable AI command menu (Cmd+J). Backed by the askAica streaming agent. */
    enableAI?: boolean;
    /**
     * Subject company org id for the askAica RAG agent (the AI command menu).
     * For the One Pager this is the deal's `assesseeOrgId`. When omitted the
     * menu falls back to the investor org.
     */
    aiOrgId?: string;
    /**
     * Extra company chips appended to the AI menu's no-selection chip row,
     * scoped to this editor instance (e.g. the compose-email pitch-draft chip).
     */
    aiExtraCompanyChips?: string[];
    /**
     * If provided, registers the @platejs/mention plugin so users can type
     * `@` to autocomplete. Each row is rendered in the combobox; on select
     * a `mention` inline node is inserted with `{ value: name, key: userId }`.
     * Pass `state.investor.ownerDropdownOptions` (or an equivalent slice).
     * Omit / pass `[]` to disable @-mentions entirely.
     */
    mentionableUsers?: MentionUser[];
    /**
     * Drops the "A4 page" content styling (72×96 padding, 640px min-height,
     * page-card border + shadow) for narrow contexts like comment inputs
     * and the compose-email body field. Keep `false` (default) for true
     * document surfaces — one-pager, engagement letter.
     */
    compact?: boolean;
    /**
     * Hide specific inline-mark buttons from the toolbar. Currently
     * recognized: 'code' | 'subscript' | 'superscript'. The marks themselves
     * stay registered as plugins so pasted content with those marks still
     * round-trips — only the toolbar UI is suppressed.
     */
    hiddenMarks?: Array<'code' | 'subscript' | 'superscript'>;
    /**
     * Opt in to block drag-and-drop reordering (the gutter drag handle).
     * When true, registers NodeIdPlugin + DndPlugin (which wraps every block
     * in <BlockDraggable>) and mounts a per-instance <DndProvider>. Default
     * `false` — leaving it off keeps the editor DOM identical to the
     * pre-feature shape so two editors can co-mount without react-dnd's
     * "two HTML5 backends" crash, and so the BlockDraggable wrapper never
     * sits between PlateContent and the slate block during PDF export.
     */
    enableBlockDnd?: boolean;
}

export const EMPTY_EDITOR_VALUE: Value = [{ type: 'p', children: [{ text: '' }] }];

/**
 * React context propagating per-editor configuration (upload source,
 * potentially more in future) to deeply-nested children — toolbar buttons,
 * element renderers — without prop-drilling through Plate's plugin system.
 */
export interface PlateEditorConfigContextValue {
    uploadSource: string;
    mentionableUsers: MentionUser[];
}
